import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { BarChart3, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function isoStart(d) { return new Date(`${d}T00:00:00`).toISOString(); }
function isoEnd(d) { return new Date(`${d}T23:59:59`).toISOString(); }

export default function Reports() {
  const today = new Date();
  const monthAgo = new Date();
  monthAgo.setDate(today.getDate() - 29);
  const [start, setStart] = useState(monthAgo.toISOString().slice(0, 10));
  const [end, setEnd] = useState(today.toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/doctors", {
        params: { start: isoStart(start), end: isoEnd(end) },
      });
      setRows(data);
    } catch (e) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const totals = rows.reduce((acc, r) => ({
    total: acc.total + (r.total || 0),
    completed: acc.completed + (r.completed || 0),
    cancelled: acc.cancelled + (r.cancelled || 0),
    no_show: acc.no_show + (r.no_show || 0),
    walk_ins: acc.walk_ins + (r.walk_ins || 0),
  }), { total: 0, completed: 0, cancelled: 0, no_show: 0, walk_ins: 0 });

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto fade-in" data-testid="reports-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 mb-2">Analytics</div>
        <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
          Doctor reports
        </h1>
        <p className="text-sm text-slate-500 mt-1">Per-doctor appointment counts for the selected date range.</p>
      </div>

      <Card className="border-slate-200 mb-6">
        <CardContent className="p-5 flex flex-wrap items-end gap-3">
          <div>
            <Label>Start date</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1.5 w-44 h-9" data-testid="report-start-input" />
          </div>
          <div>
            <Label>End date</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1.5 w-44 h-9" data-testid="report-end-input" />
          </div>
          <Button onClick={load} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="run-report-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { k: "total", label: "Total", color: "text-slate-900" },
          { k: "completed", label: "Completed", color: "text-emerald-700" },
          { k: "scheduled", label: "", color: "" }, // skip
          { k: "cancelled", label: "Cancelled", color: "text-red-700" },
          { k: "no_show", label: "No-show", color: "text-amber-700" },
          { k: "walk_ins", label: "Walk-ins", color: "text-blue-700" },
        ].filter((x) => x.label).map((x) => (
          <Card key={x.k} className="border-slate-200" data-testid={`kpi-${x.k}`}>
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wide">{x.label}</div>
              <div className={`text-2xl font-bold mt-1 ${x.color}`} style={{ fontFamily: "Manrope" }}>{totals[x.k]}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Doctor</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Completed</TableHead>
                <TableHead className="text-right">Cancelled</TableHead>
                <TableHead className="text-right">No-show</TableHead>
                <TableHead className="text-right">Walk-ins</TableHead>
                <TableHead className="text-right">Completion rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-400 py-12" data-testid="reports-empty">
                    <BarChart3 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    No appointment data for this range yet
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => {
                const rate = r.total ? Math.round((r.completed / r.total) * 100) : 0;
                return (
                  <TableRow key={r.doctor_id} data-testid={`report-row-${r.doctor_id}`}>
                    <TableCell className="font-medium">{r.doctor_name || r.doctor_id}</TableCell>
                    <TableCell className="text-right">{r.total}</TableCell>
                    <TableCell className="text-right text-emerald-700">{r.completed}</TableCell>
                    <TableCell className="text-right text-red-700">{r.cancelled}</TableCell>
                    <TableCell className="text-right text-amber-700">{r.no_show}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-700">{r.walk_ins}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold">{rate}%</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
